import salaryService from 'ApiService/Accountant/salaryService';
import { AuthContext } from 'contexts/AuthContext';
import { useContext, useEffect, useState } from 'react';
import ConfirmationModal from 'pages/Common/ConfirmationModal';

const SalaryDetails = ({ employeeData, onSalaryCreated }) => {
  const [employeeSalaryDetails, setEmployeeSalaryDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    currentSalary: '',
    bankDetails: {
      accountNumber: '',
      ifscCode: '',
      bankName: '',
    },
  });
  const [error, setError] = useState('');
  const [isModalOpenConfirmation, setIsModalOpenConfirmation] = useState(false);
  const { state } = useContext(AuthContext);

  const fetchEmployeeSalaryDetails = async () => {
    try {
      const selectedBranch = state?.selectedBranch || '';
      setLoading(true);
      setError('');
      const response = await salaryService.getEmployeeSalaryDetails({
        employeeId: employeeData._id,
        year: new Date().getFullYear(),
        selectedBranch,
      });

      if (response.data.success) {
        setEmployeeSalaryDetails(response.data.data);
        setEditData({
          currentSalary: response.data.data.currentSalary,
          bankDetails: response.data.data.bankDetails,
        });
      } else {
        setError('Failed to fetch salary details');
      }
    } catch (error) {
      setError(
        error?.response?.data?.message || 'Failed to fetch salary details'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (employeeData) {
      fetchEmployeeSalaryDetails();
    }
  }, [employeeData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('bank.')) {
      const field = name.split('.')[1];
      setEditData((prev) => ({
        ...prev,
        bankDetails: {
          ...prev.bankDetails,
          [field]: value,
        },
      }));
    } else {
      setEditData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const Confirmation = (e) => {
    e.preventDefault();
    setIsModalOpenConfirmation(true);
  };

  const handleSubmit = async () => {
    try {
      setError('');
      const selectedBranch = state?.selectedBranch || '';
      const payload = {
        selectedBranch,
        employeeId: employeeData._id,
        // currentSalary: editData.currentSalary,
        bankDetails: editData.bankDetails,
      };
      const response = await salaryService.updateEmployeeSalaryDetails(payload);
      if (response.data.success) {
        setEmployeeSalaryDetails(response.data.salary);
        setIsEditing(false);
        setIsModalOpenConfirmation(false);
        if (onSalaryCreated) onSalaryCreated();
      } else {
        setError('Failed to update salary');
      }
    } catch (error) {
      setError(error?.response?.data?.message || 'Failed to update salary');
    }
  };

  const maskAccountNumber = (accountNumber) => {
    if (!accountNumber) return '';
    const visible = accountNumber.slice(-4);
    const masked = accountNumber.slice(0, -4).replace(/./g, 'X');
    return masked + visible;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="text-sm">Loading...</div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl rounded-lg bg-white p-4 shadow-sm dark:bg-navy-800 ">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-white ">
          Salary Details
        </h2>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white transition-colors hover:bg-blue-700"
          >
            Edit
          </button>
        )}
      </div>

      {error && (
        <div className="mb-3 rounded border border-red-200 bg-red-50 p-2 text-sm text-red-600">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-4">
          <div>
            <h3 className="mb-2 text-sm font-medium text-gray-700">
              Basic Info
            </h3>
            <div className="space-y-2 text-sm">
              <div className="grid grid-cols-2 gap-1">
                <div className="text-gray-600">Name:</div>
                <div className="text-gray-800 dark:text-gray-600">
                  {employeeSalaryDetails?.employeeId?.name}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-1">
                <div className="text-gray-600">Type:</div>
                <div className="text-gray-800 dark:text-gray-600">
                  {employeeSalaryDetails?.employeeType === 'Account'
                    ? 'Accountant'
                    : employeeSalaryDetails?.employeeType === 'Placement'
                    ? 'Placement Officer'
                    : employeeSalaryDetails?.employeeType}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-1">
                <div className="text-gray-600">Current Salary:</div>
                <div>
                  {/* {isEditing ? (
                    <input
                      type="number"
                      name="currentSalary"
                      value={editData.currentSalary}
                      onChange={handleInputChange}
                      className="w-full rounded border px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-navy-700 dark:text-white"
                    />
                  ) : ( */}
                  <span className="text-gray-800 dark:text-gray-600">
                    {employeeSalaryDetails?.currentSalary}
                  </span>
                  {/* )} */}
                </div>
              </div>
            </div>
          </div>

          <div>
            <h3 className="mb-2 text-sm font-medium text-gray-700">
              Bank Details
            </h3>
            <div className="space-y-2 text-sm">
              {isEditing ? (
                <div className="space-y-2">
                  <div>
                    <label className="mb-1 block text-xs text-gray-600">
                      Account Number
                    </label>
                    <input
                      type="text"
                      name="bank.accountNumber"
                      value={editData.bankDetails.accountNumber}
                      onChange={handleInputChange}
                      className="w-full rounded border px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-navy-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-gray-600">
                      IFSC Code
                    </label>
                    <input
                      type="text"
                      name="bank.ifscCode"
                      value={editData.bankDetails.ifscCode}
                      onChange={handleInputChange}
                      className="w-full rounded border px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-navy-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-gray-600">
                      Bank Name
                    </label>
                    <input
                      type="text"
                      name="bank.bankName"
                      value={editData.bankDetails.bankName}
                      onChange={handleInputChange}
                      className="w-full rounded border px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-navy-700 dark:text-white"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-1">
                    <div className="text-gray-600">Account:</div>
                    <div className="text-gray-800 dark:text-gray-600 ">
                      {employeeSalaryDetails?.bankDetails?.accountNumber}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <div className="text-gray-600">IFSC:</div>
                    <div className="text-gray-800 dark:text-gray-600">
                      {employeeSalaryDetails?.bankDetails?.ifscCode}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <div className="text-gray-600">Bank:</div>
                    <div className="text-gray-800 dark:text-gray-600">
                      {employeeSalaryDetails?.bankDetails?.bankName}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div>
          <h3 className="mb-2 text-sm font-medium text-gray-700">
            Salary History
          </h3>
          <div className="rounded border dark:text-gray-600">
            <div className="max-h-64 overflow-y-auto">
              {employeeSalaryDetails?.salaryHistory.map((record, index) => (
                <div
                  key={index}
                  className="border-b p-2 text-sm last:border-b-0"
                >
                  <div className="flex justify-between">
                    <div className="font-medium">₹{record.amount}</div>
                    <div className="text-xs text-gray-600">
                      {new Date(record.date).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {isEditing && (
        <div className="mt-4 flex space-x-2">
          <button
            onClick={Confirmation}
            className="rounded bg-green-600 px-3 py-1.5 text-sm text-white transition-colors hover:bg-green-700"
          >
            Save
          </button>
          <button
            onClick={() => setIsEditing(false)}
            className="rounded bg-gray-200 px-3 py-1.5 text-sm text-gray-800 transition-colors hover:bg-gray-300"
          >
            Cancel
          </button>
        </div>
      )}

      <ConfirmationModal
        isOpen={isModalOpenConfirmation}
        onClose={() => setIsModalOpenConfirmation(false)}
        onConfirm={handleSubmit}
        title="Confirm Bank Details Update"
        message={
          <div className="space-y-3 text-left">
            <p className="text-lg font-semibold text-navy-700 dark:text-white">
              Please confirm the following bank details update:
            </p>
            <div className="mt-3 grid grid-cols-2 gap-2 rounded-lg bg-gray-50 p-3 dark:bg-navy-900">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Employee:
              </span>
              <span className="text-sm text-navy-700 dark:text-white">
                {employeeSalaryDetails?.employeeId?.name || 'Selected Employee'}
              </span>

              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Current Salary:
              </span>
              <span className="text-sm text-navy-700 dark:text-white">
                ₹{employeeSalaryDetails?.currentSalary.toLocaleString() || '0'}
              </span>

              <span className="col-span-2 mt-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                New Bank Details:
              </span>

              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Bank Name:
              </span>
              <span className="text-sm text-navy-700 dark:text-white">
                {editData.bankDetails.bankName || 'Not provided'}
              </span>

              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Account Number:
              </span>
              <span className="text-sm text-navy-700 dark:text-white">
                {maskAccountNumber(editData.bankDetails.accountNumber) ||
                  'Not provided'}
              </span>

              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                IFSC Code:
              </span>
              <span className="text-sm text-navy-700 dark:text-white">
                {editData.bankDetails.ifscCode || 'Not provided'}
              </span>
            </div>
            <p className="mt-2 text-sm text-red-500">
              Please verify all details before confirming. This will update the
              bank details for the employee.
            </p>
          </div>
        }
        confirmText="Update Details"
        cancelText="Cancel"
        icon="bank"
      />
    </div>
  );
};

export default SalaryDetails;
